<template>
  <section
    ref="section"
    class="cn-search-header"
    :class="{
      'cn-search-header--filled': searchQuery,
    }"
  >
    <div class="cn-search-header__lines">
      <div class="line line-blue" />
      <div class="line line-blue" />
    </div>
    <p></p>
    <div class="cn-search-header__content">
      <div class="cn-search-header__container container">
        <div class="section-inner">
          <div class="cn-search-header__text-and-form">
            <h1 class="title">
              {{ title }}
            </h1>
            <p v-if="description" class="description">
              {{ description }}
            </p>
            <div class="cn-search-header__form">
              <UiSearchForm
                v-model="searchText"
                button-variant="aqua"
                @search="search"
              />
            </div>
          </div>
          <div class="cn-search-header__boxes">
            <UiParallelogram
              color="aqua"
              class="cn-search-header__parallelogram-1"
            />
            <UiParallelogram
              color="aqua"
              class="cn-search-header__parallelogram-2"
            />
            <UiParallelogram
              color="aqua"
              class="cn-search-header__parallelogram-3"
            />
            <UiParallelogram
              color="aqua"
              class="cn-search-header__parallelogram-4"
            />
            <UiParallelogram
              color="aqua"
              class="cn-search-header__parallelogram-5"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, watchEffect } from 'vue';
import UiSearchForm from '@/components/ui/UiSearchForm';
import UiParallelogram from '@/components/ui/UiParallelogram';

interface Props {
  title?: string;
  description?: string;
}
interface Emits {
  (e: 'search', data: string): void;
}

defineProps<Props>();
const emit = defineEmits<Emits>();

const router = useRouter();
const route = useRoute();
const searchQuery = computed(() => (route.query.search as string) ?? '');
const searchText = ref('');

const search = (searchString: string | undefined) => {
  router.push({
    query: {
      search: searchString || undefined,
    },
  });
  emit('search', searchText.value);
};
watchEffect(() => {
  searchText.value = searchQuery.value;
});
</script>

<style lang="scss">
.cn-search-header {
  --ab-canyon-search-header-color-text: var(--ab-canyon-color-light);
  --ab-canyon-search-header-color-bg: var(--ab-canyon-color-primary);

  color: var(--ab-canyon-search-header-color-text);
  background-color: var(--ab-canyon-search-header-color-bg);
  overflow: hidden;
  position: relative;
  &__lines {
    justify-content: flex-end;
    transform: translate(-150px, 0);
    width: 100%;
    height: 100%;
    display: flex;
    position: absolute;
    .line {
      &:nth-child(1) {
        transform: rotate(210deg) skewY(-30deg) scaleX(1.16) scaleY(1.16);
        width: 600px;
        height: 100%;
      }
      &:nth-child(2) {
        transform: rotate(30deg) skewY(-30deg) scaleX(1.16) scaleY(1.16);
        width: 500px;
        height: 100%;
      }
    }
  }
  &__content {
    height: 100%;
  }
  &__container {
    align-items: center;
    height: 100%;
    display: flex;
    position: relative;
    .section-inner {
      width: 100%;
    }
  }
  &__text-and-form {
    z-index: 10;
    position: relative;
    .title {
      text-transform: uppercase;
      font-weight: 700;
    }
    .description {
      transition-property: all;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      transition-duration: 150ms;
      font-size: 1.125rem;
      line-height: 1.75rem;
      max-width: calc(48 * var(--size-base));
      margin-top: var(--size-xl);
      @media (min-width: 1656px) {
        font-size: 1.5rem;
        line-height: 2rem;
      }

      @media (min-width: 656px) {
        font-size: 1.25rem;
        line-height: 1.75rem;
        margin-top: var(--size-5xl);
      }
    }
  }
  &__form {
    margin-top: var(--size-lg);
    @media (min-width: 1656px) {
      margin-top: 1.75rem;
    }
  }
  &__boxes {
    display: none;
    @media (min-width: 656px) {
      display: block;
    }
  }
  &__parallelogram-1 {
    border-width: 3px;
    width: 65px;
    height: 100px;
    top: 40px;
    right: 180px;
    position: absolute;
  }
  &__parallelogram-2 {
    border-width: 2px;
    width: 40px;
    height: 60px;
    top: 140px;
    right: 320px;
    position: absolute;
  }
  &__parallelogram-3 {
    border-width: 3px;
    width: 170px;
    height: 260px;
    top: 300px;
    right: 100px;
    position: absolute;
  }
  &__parallelogram-4 {
    border-width: 2px;
    width: 25px;
    height: 40px;
    top: 520px;
    right: 0px;
    position: absolute;
  }
  &__parallelogram-5 {
    border-width: 2px;
    width: 55px;
    height: 85px;
    top: 570px;
    right: 460px;
    position: absolute;
  }
  > p {
    font-weight: 300;
    font-size: 1.125rem;
    line-height: 1.75rem;
  }
  &:not(.cn-search-header--filled) {
    min-height: 500px;
    height: calc(100vh - 64px);
    @media (min-width: 1656px) {
      height: calc(100vh - 121px);
    }
    .cn-search-header {
      &__container {
        .section-inner {
          padding-top: calc(4 * var(--size-base));
          padding-bottom: calc(8 * var(--size-base));
        }
      }
      &__text-and-form {
        .title {
          font-size: 1.5rem;
          line-height: 2rem;

          @media (min-width: 656px) {
            font-size: 2.25rem;
            line-height: 2.5rem;
          }
          @media (min-width: 1656px) {
            font-size: 3rem;
            line-height: 1;
          }
        }
      }
    }
  }
  &--filled {
    .cn-search-header {
      &__container {
        .section-inner {
          padding-top: var(--size-4xl);
          padding-bottom: var(--size-4xl);
        }
      }
      &__text-and-form {
        .title {
          font-size: 1.5rem;
          line-height: 2rem;
          @media (min-width: 656px) {
            font-size: 1.875rem;
            line-height: 2.25rem;
          }
          @media (min-width: 1656px) {
            font-size: 2.25rem;
            line-height: 2.5rem;
          }
        }
        .description {
          display: none;
        }
      }
    }
  }
}
</style>
